/**
 * The device-token registry at `users/{uid}/fcm_tokens/{token}`.
 *
 * Split out of use-push-registration so the sign-out path can reach the
 * forget/prune half without importing a React hook.
 *
 * The rule this module exists to keep: an FCM registration token identifies a
 * BROWSER, not an account. It therefore has to be removed when the account
 * stops owning the browser, or a notification addressed to the person who
 * signed out is delivered to the person who signed in.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "integrations/firebase/firestore";
import {
  currentPushToken,
  deletePushToken,
} from "integrations/firebase/messaging";

/** A device not seen for this long is gone: reinstalled, wiped, or replaced.
 *  Well inside FCM's own ~270-day staleness window, so the prune removes rows
 *  we would otherwise carry for ever, never rows still in use. */
const STALE_TOKEN_DAYS = 60;

/** One page is plenty: a user has a handful of devices, and the cap stops a
 *  pathological subtree from turning a page load into a long delete loop. */
const PRUNE_LIMIT = 20;

/** How long the sign-out will wait for push cleanup before going ahead
 *  without it. Long enough for a round trip on conference wifi, short enough
 *  that nobody reads the Sign out button as broken.
 *
 *  It covers the two steps that must finish BEFORE `signOut` — the token
 *  lookup and the row delete — and they share it rather than getting a slice
 *  each. They run in sequence, so one deadline over both is the arrangement
 *  that admits the most work: any fixed slice would cap a lookup that could
 *  have finished, or a delete, inside the same total. The FCM surrender is not
 *  on this clock at all; see `forgetPushToken`. */
const CLEANUP_BUDGET_MS = 3_000;

/**
 * Resolve when `work` settles or when the budget runs out, whichever is first,
 * and never reject.
 *
 * Both halves are needed, and only the first was there before. A rejection
 * must not propagate, because push cleanup is best effort — but neither must a
 * promise that never settles. `deleteDoc` resolves on SERVER acknowledgement:
 * with no network the Firestore SDK applies the mutation to the local cache
 * and leaves the returned promise pending until connectivity returns, which
 * may be never. Awaiting that on the sign-out path is a Sign out button that
 * does nothing, for ever, with no spinner and no error — both call sites are
 * fire-and-forget.
 */
export async function settledWithin<T>(
  work: Promise<T>,
  budgetMs: number,
): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work.catch(() => undefined),
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), budgetMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function tokenCollection(uid: string) {
  return collection(db, "users", uid, "fcm_tokens");
}

export async function savePushToken(uid: string, token: string): Promise<void> {
  const tokenRef = doc(db, "users", uid, "fcm_tokens", token);
  try {
    // The common case: this device registered before, only lastSeenAt may
    // move (rules pin every other field). Trying update first also avoids
    // rewriting createdAt on every visit.
    await updateDoc(tokenRef, { lastSeenAt: serverTimestamp() });
  } catch {
    // userAgent is optional in the rules AND must be non-empty when present,
    // so a privacy-hardened browser reporting "" must omit the field — an
    // empty string would fail the create and silently kill push for the
    // device (permission already granted, banner never shown again).
    const userAgent = navigator.userAgent.slice(0, 300);
    await setDoc(tokenRef, {
      token,
      createdAt: serverTimestamp(),
      lastSeenAt: serverTimestamp(),
      ...(userAgent ? { userAgent } : {}),
    });
  }
}

/**
 * Drop this browser's registration for `uid`, both the Firestore row and the
 * FCM token itself.
 *
 * Never rejects AND always settles, within `budgetMs`. Sign-out has to proceed
 * even when push cleanup cannot — the alternative is a user stuck signed in
 * because a service worker was unavailable or the network was gone.
 *
 * Only the token lookup and the Firestore row delete are on that clock,
 * because only they have to be finished before `signOut` revokes the
 * credential the delete needs. They share the one deadline: they run in
 * sequence, so a slice each would only cap a step that could have finished
 * inside the same total.
 *
 * The FCM surrender is detached, and deliberately unbounded.
 * `requestDeleteToken` authenticates with the Installations (FIS) header
 * rather than the user's ID token, so it is unaffected by `signOut` and there
 * is no deadline to hold it to. Waiting for it, as this did, only delayed the
 * sign-out by an FCM round trip — the whole remaining budget when the network
 * was slow — and bought nothing, since nothing here acts on the result.
 *
 * (An earlier revision kept it inside the budget on the grounds that a
 * detached cleanup is guaranteed to fail once `signOut` revokes the
 * credential. That is true of the Firestore delete and false of the surrender.
 * Note also that neither arrangement can cancel a surrender already in flight:
 * a budget that runs out stops this function waiting, it does not stop the
 * DELETE.)
 *
 * The two halves are guarded independently. Sharing one `try` meant a rules,
 * quota or transient-503 failure on the row delete skipped the surrender, so
 * the row survived *and* the token string survived and the next account on
 * this browser inherited it — which is the defect this module exists to close.
 *
 * A row delete that does not finish inside the budget is lost: the mutation
 * sits in the SDK's local write queue, and the rules refuse it once the owner
 * has signed out. The orphan is deleted by the backend the first time FCM
 * reports the token gone (PushTokenGoneError). A sign-out with no network at
 * all lands neither half — `deleteToken` performs a network DELETE before it
 * clears anything locally, so a failed surrender leaves the very same token
 * string in place — and that residue is covered by the cross-user token
 * registration fence tracked in parity-plan.md.
 *
 * `budgetMs` is a parameter for the same reason `prunePushTokens` takes `now`:
 * so a test can assert the bound without waiting for it.
 */
export async function forgetPushToken(
  uid: string,
  budgetMs: number = CLEANUP_BUDGET_MS,
): Promise<void> {
  const deadline = Date.now() + budgetMs;
  const remaining = () => Math.max(0, deadline - Date.now());

  // Strictly before the surrender: deleteToken makes the next getToken mint a
  // DIFFERENT string, so a lookup racing it could hand back a fresh token that
  // no Firestore row names.
  const token = await settledWithin(currentPushToken(), remaining());

  // Unconditional: the token may be registered at FCM even when this browser's
  // row cannot be found or deleted. Issued before the row delete rather than
  // beside it, so no failure of the delete can reach it.
  void deletePushToken().catch(() => undefined);

  if (token) {
    await settledWithin(
      deleteDoc(doc(db, "users", uid, "fcm_tokens", token)),
      remaining(),
    );
  }
}

/** How long turning push off waits for each half before calling it failed.
 *  Longer than the sign-out budget: somebody is looking at a toggle that
 *  says "Turning off…", and nothing else is waiting on it. */
const TURN_OFF_BUDGET_MS = 10_000;

/**
 * Turn push off for this browser while `uid` stays signed in: the switch on
 * the Profile page and in the inbox.
 *
 * Both halves of `forgetPushToken`, with the result reported instead of
 * swallowed. The sender reads every row under `users/{uid}/fcm_tokens`, so
 * the row delete is what stops the pushes; the FCM surrender makes the token
 * string dead as well, so a row that survives (a delete still queued offline)
 * is removed by the backend the first time FCM reports the token gone. Either
 * half landing is enough, which is why this returns true when at least one
 * did. The browser's notification permission is left alone: turning push
 * back on mints a fresh token without a prompt.
 *
 * The lookup finishes before the surrender starts, for the reason given in
 * `forgetPushToken`: a lookup racing `deleteToken` can mint a new string that
 * no row names.
 */
export async function unregisterPushToken(
  uid: string,
  budgetMs: number = TURN_OFF_BUDGET_MS,
): Promise<boolean> {
  const token = await settledWithin(currentPushToken(), budgetMs);
  const [rowGone, fcmGone] = await Promise.all([
    token
      ? settledWithin(
          deleteDoc(doc(db, "users", uid, "fcm_tokens", token)).then(
            () => true,
          ),
          budgetMs,
        )
      : Promise.resolve(false),
    settledWithin(
      deletePushToken().then(() => true),
      budgetMs,
    ),
  ]);
  return rowGone === true || fcmGone === true;
}

/**
 * Remove the caller's own long-dead device rows.
 *
 * Opportunistic. Its only caller is the registration in
 * PushRegistrationProvider, mounted once at the router root, so this runs
 * once per page load for a signed-in user with push granted, whichever page
 * they open. The browser in front of you is safe, because the same effect
 * refreshes this device's lastSeenAt first.
 *
 * Tokens belonging to someone who has stopped using Connect entirely are left
 * to the backend, which deletes a row the moment FCM reports it gone
 * (PushTokenGoneError).
 */
export async function prunePushTokens(
  uid: string,
  now: Date = new Date(),
): Promise<number> {
  const cutoff = Timestamp.fromDate(
    new Date(now.getTime() - STALE_TOKEN_DAYS * 24 * 60 * 60 * 1000),
  );
  try {
    const stale = await getDocs(
      query(
        tokenCollection(uid),
        where("lastSeenAt", "<", cutoff),
        limit(PRUNE_LIMIT),
      ),
    );
    await Promise.all(stale.docs.map((snapshot) => deleteDoc(snapshot.ref)));
    return stale.docs.length;
  } catch {
    return 0;
  }
}
